import { useCallback, useEffect, useRef, useState } from 'react';
import type { DataTableExpansion } from './types';

/**
 * Expand / full-screen state for a DataTable.
 *
 * The state lives in a hook the *app* calls, not inside `<DataTable>`, because
 * consumers read `expanded` synchronously in the same render to compute the
 * page size they fetch with. State hidden inside the component would only be
 * observable a render late.
 *
 * Focus handling is deliberately *soft*: focus moves into the overlay on
 * expand and returns to the toggle on collapse, Escape collapses, but there
 * is no focus trap and the rest of the page is not `aria-hidden`. The overlay
 * is a `region`, not a `dialog` - the app's nav sidebar stays keyboard
 * reachable on purpose.
 */
export function useDataTableExpansion(initialExpanded = false): DataTableExpansion {
    const [expanded, setExpanded] = useState(initialExpanded);
    const overlayRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const restoreRef = useRef<HTMLElement | null>(null);
    const hasExpandedRef = useRef(false);

    const expand = useCallback(() => {
        restoreRef.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
        hasExpandedRef.current = true;
        setExpanded(true);
    }, []);

    const collapse = useCallback(() => setExpanded(false), []);

    const toggle = useCallback(() => {
        if (expanded) {
            collapse();
        } else {
            expand();
        }
    }, [expanded, expand, collapse]);

    // Escape collapses from anywhere, including from inside a cell's popover
    // (which may render outside the overlay's DOM subtree), so the listener
    // goes on the document rather than the overlay.
    useEffect(() => {
        if (!expanded) {
            return;
        }
        overlayRef.current?.focus();
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setExpanded(false);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [expanded]);

    // Return focus on collapse - but not on the initial mount, where nothing
    // was ever taken away.
    useEffect(() => {
        if (expanded || !hasExpandedRef.current) {
            return;
        }
        const target = triggerRef.current ?? restoreRef.current;
        target?.focus();
    }, [expanded]);

    return { expanded, expand, collapse, toggle, overlayRef, triggerRef };
}
